import ApiService from './api.service'
import { read, utils, write } from 'xlsx'

const FALLBACK_FILENAME = 'downloaded-file'

const EXCEL_FORMATS = {
  xlsx: {
    bookType: 'xlsx',
    extension: '.xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  },
  xls: {
    bookType: 'xls',
    extension: '.xls',
    mimeType: 'application/vnd.ms-excel'
  }
}

const normalizeExcelFormat = (format = 'xlsx') => {
  return String(format).toLowerCase() === 'xls' ? 'xls' : 'xlsx'
}

const parseFileNameFromDisposition = (contentDisposition = '') => {
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      return utf8Match[1].trim()
    }
  }

  const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  return basicMatch && basicMatch[1] ? basicMatch[1] : FALLBACK_FILENAME
}

const parseFileNameFromUrl = (fileUrl = '') => {
  if (!fileUrl) {
    return FALLBACK_FILENAME
  }

  try {
    const parsedUrl = new URL(fileUrl)
    const fileName = decodeURIComponent(parsedUrl.pathname.split('/').pop() || '')
    return fileName || FALLBACK_FILENAME
  } catch {
    const fileName = decodeURIComponent(String(fileUrl).split('/').pop() || '')
    return fileName || FALLBACK_FILENAME
  }
}

const triggerBlobDownload = (blobData, fileName) => {
  const blobUrl = window.URL.createObjectURL(blobData)
  const anchor = document.createElement('a')
  anchor.href = blobUrl
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(blobUrl)
}

const parseBlobJsonMessage = async (response) => {
  if (!response || ![400, 422].includes(response.status) || !(response.data instanceof Blob)) {
    return null
  }

  const contentType = response.headers?.['content-type'] || ''
  if (!contentType.includes('application/json')) {
    return null
  }

  try {
    const text = await response.data.text()
    const parsed = JSON.parse(text)
    return parsed?.message || parsed?.data?.message || null
  } catch {
    return null
  }
}

const normalizeExportHeaders = (headers = []) => {
  if (!Array.isArray(headers)) {
    return []
  }

  return headers.filter((header) => typeof header === 'string' && header.trim()).map((header) => header.trim())
}

const normalizeExportRows = (rows = []) => {
  if (!Array.isArray(rows)) {
    return []
  }

  return rows.filter((row) => row && typeof row === 'object' && !Array.isArray(row))
}

const fileNameToExcel = (fileName = FALLBACK_FILENAME, format = 'xlsx') => {
  const trimmed = String(fileName).trim() || FALLBACK_FILENAME
  const withoutExtension = trimmed.replace(/\.(csv|xlsx|xls)$/i, '')
  const excelFormat = normalizeExcelFormat(format)
  return `${withoutExtension}${EXCEL_FORMATS[excelFormat].extension}`
}

const CSV_MIME_TYPE = 'text/csv;charset=utf-8'

const isCsvContentType = (contentType = '') => {
  const normalized = String(contentType).toLowerCase()
  return normalized.includes('csv') || normalized.includes('text/plain')
}

const readWorkbookFromBlob = async (blob) => {
  const buffer = await blob.arrayBuffer()
  return read(buffer, { type: 'array' })
}

const normalizeExportFormat = (format = 'xlsx') => {
  const normalized = String(format).toLowerCase()
  if (normalized === 'csv') {
    return 'csv'
  }
  return normalizeExcelFormat(format)
}

const FileDownloadService = {
  async downloadExcelFromCsvBlob(csvBlob, fileName = FALLBACK_FILENAME, format = 'xlsx') {
    if (!csvBlob) {
      return {
        success: false,
        message: 'Missing CSV data'
      }
    }

    try {
      const csvText = await (csvBlob instanceof Blob ? csvBlob.text() : Promise.resolve(String(csvBlob)))
      const normalizedCsv = csvText.replace(/^\uFEFF/, '')
      const workbook = read(normalizedCsv, { type: 'string' })
      const firstSheetName = workbook.SheetNames[0]

      if (!firstSheetName) {
        return {
          success: false,
          message: 'CSV file is empty'
        }
      }

      const excelFormat = normalizeExcelFormat(format)
      const formatConfig = EXCEL_FORMATS[excelFormat]
      const excelBuffer = write(workbook, {
        bookType: formatConfig.bookType,
        type: 'array',
        compression: excelFormat === 'xlsx'
      })
      const finalFileName = fileNameToExcel(fileName, excelFormat)
      const blob = new Blob([excelBuffer], {
        type: formatConfig.mimeType
      })

      triggerBlobDownload(blob, finalFileName)

      const sheet = workbook.Sheets[firstSheetName]
      const rowCount = Math.max(0, utils.sheet_to_json(sheet, { header: 1 }).length - 1)

      return {
        success: true,
        fileName: finalFileName,
        totalRows: rowCount
      }
    } catch {
      return {
        success: false,
        message: 'Failed to convert CSV to Excel'
      }
    }
  },
  async downloadExportBlob(blob, fileName = FALLBACK_FILENAME, format = 'xlsx', contentType = '') {
    if (!blob) {
      return {
        success: false,
        message: 'Missing export data'
      }
    }

    try {
      const normalizedFormat = normalizeExportFormat(format)
      const baseName = String(fileName).trim().replace(/\.(csv|xlsx|xls)$/i, '') || FALLBACK_FILENAME

      if (normalizedFormat === 'csv') {
        if (isCsvContentType(contentType)) {
          triggerBlobDownload(blob, `${baseName}.csv`)
          return { success: true, fileName: `${baseName}.csv` }
        }

        const workbook = await readWorkbookFromBlob(blob)
        const sheetName = workbook.SheetNames[0]

        if (!sheetName) {
          return {
            success: false,
            message: 'Export file is empty'
          }
        }

        const csv = utils.sheet_to_csv(workbook.Sheets[sheetName])
        const csvBlob = new Blob([`\uFEFF${csv}`], { type: CSV_MIME_TYPE })
        triggerBlobDownload(csvBlob, `${baseName}.csv`)

        return { success: true, fileName: `${baseName}.csv` }
      }

      if (isCsvContentType(contentType)) {
        return this.downloadExcelFromCsvBlob(blob, baseName, normalizedFormat)
      }

      const workbook = await readWorkbookFromBlob(blob)
      const sheetName = workbook.SheetNames[0]

      if (!sheetName) {
        return {
          success: false,
          message: 'Export file is empty'
        }
      }

      const formatConfig = EXCEL_FORMATS[normalizedFormat]
      const excelBuffer = write(workbook, {
        bookType: formatConfig.bookType,
        type: 'array',
        compression: normalizedFormat === 'xlsx'
      })
      const finalFileName = `${baseName}${formatConfig.extension}`
      const excelBlob = new Blob([excelBuffer], { type: formatConfig.mimeType })

      triggerBlobDownload(excelBlob, finalFileName)

      return {
        success: true,
        fileName: finalFileName
      }
    } catch {
      return {
        success: false,
        message: 'Failed to prepare export file'
      }
    }
  },
  async downloadAzurePrivateFile(privateUrl) {
    if (!privateUrl) {
      return {
        success: false,
        message: 'Missing file URL'
      }
    }

    try {
      const response = await ApiService.post(
        'admin/azure/private-file/download',
        { url: privateUrl },
        {
          responseType: 'blob',
          'Content-Type': 'application/json'
        }
      )

      const contentType = response.headers?.['content-type'] || 'application/octet-stream'
      const dispositionFileName = parseFileNameFromDisposition(response.headers?.['content-disposition'] || '')
      const fileName = dispositionFileName !== FALLBACK_FILENAME ? dispositionFileName : parseFileNameFromUrl(privateUrl)
      const blob = new Blob([response.data], { type: contentType })

      triggerBlobDownload(blob, fileName)

      return { success: true }
    } catch (error) {
      const message = (await parseBlobJsonMessage(error?.response)) || error?.response?.data?.message || null
      return {
        success: false,
        message
      }
    }
  },

  downloadExcelFromRows({ headers = [], rows = [], fileName = FALLBACK_FILENAME, format = 'xlsx' } = {}) {
    const normalizedHeaders = normalizeExportHeaders(headers)
    const normalizedRows = normalizeExportRows(rows)

    if (!normalizedHeaders.length) {
      return {
        success: false,
        message: 'Missing export headers'
      }
    }

    const worksheetData = [
      normalizedHeaders,
      ...normalizedRows.map((row) => normalizedHeaders.map((header) => row?.[header] ?? ''))
    ]

    const worksheet = utils.aoa_to_sheet(worksheetData)
    const workbook = utils.book_new()
    utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    const excelFormat = normalizeExcelFormat(format)
    const formatConfig = EXCEL_FORMATS[excelFormat]
    const excelBuffer = write(workbook, {
      bookType: formatConfig.bookType,
      type: 'array',
      compression: excelFormat === 'xlsx'
    })

    const normalizedFileName = String(fileName).trim() || FALLBACK_FILENAME
    const finalFileName = fileNameToExcel(normalizedFileName, excelFormat)
    const blob = new Blob([excelBuffer], {
      type: formatConfig.mimeType
    })

    triggerBlobDownload(blob, finalFileName)

    return {
      success: true,
      totalRows: normalizedRows.length
    }
  }
}

export { FileDownloadService }
